<template>
  <div class="guide-ai-agents">
    <p class="guide-lead">
      eSign Launchpad ships a first-class Model Context Protocol (MCP) server that lets
      AI agents, Claude Desktop, Claude Code, Cursor, and any other MCP-compatible
      client, create packages, upload documents, add signers, place fields, send
      ceremonies, and track signing progress in natural conversation. No custom API
      client required. The agent discovers the full tool catalog at connection time.
    </p>

    <q-separator class="q-my-lg" />

    <h2>What is MCP?</h2>
    <p>
      The <a href="https://modelcontextprotocol.io" target="_blank" rel="noopener">Model Context Protocol</a>
      is an open standard that lets AI agents discover and call tools from external
      services over a simple stdio or network transport. An MCP server exposes a set of
      named tools, each with an input schema and description. The agent introspects the
      catalog and picks the right tool automatically based on what the user asks for in
      chat.
    </p>
    <p>
      eSign Launchpad's MCP server is published at
      <a href="https://www.npmjs.com/package/@esignlaunchpad/mcp-server" target="_blank" rel="noopener"><code>@esignlaunchpad/mcp-server</code></a>
      on npm. It reads the platform's OpenAPI specification at startup and generates
      over 150 tools covering packages, documents, signers, signing fields, templates,
      signing groups, webhooks, audit trails, and more. Because tools come from the live
      spec, the agent's capabilities track the API exactly. New endpoints appear as new
      tools the next time the server starts.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Prerequisites</h2>
    <ul>
      <li>
        A <strong>Personal Access Token</strong> from <strong>Settings</strong> then
        <strong>API</strong>. Click <strong>Generate API Key</strong>, choose
        <em>Personal Access Token</em>, pick the scopes your agent needs, and copy the
        <code>pat_...</code> string shown once. The MCP server uses a PAT rather than
        OAuth client credentials so there is no token endpoint round-trip on every
        agent invocation. See
        <a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>
        for scope selection.
      </li>
      <li>
        <strong>Node.js 18 or newer</strong> on the machine running your AI client. The
        MCP server runs as an <code>npx</code>-invoked subprocess in your agent's stdio
        pipeline. No global install is required.
      </li>
      <li>
        A <strong>Business-tier subscription</strong>. API access is a Business-tier
        feature, and every MCP call counts as an API call.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Connecting your AI agent</h2>

    <h3>Claude Desktop</h3>
    <p>
      Add the following to <code>claude_desktop_config.json</code>. On macOS that file
      lives at <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>.
      On Windows it is at <code>%APPDATA%\Claude\claude_desktop_config.json</code>.
      Restart Claude Desktop after saving.
    </p>
    <GuideCodeTabs :samples="samples.claudeDesktop" />

    <h3>Claude Code</h3>
    <p>
      Drop a <code>.mcp.json</code> at your project root using the same shape as Claude
      Desktop's <code>mcpServers</code> block. Claude Code picks it up per-project, so
      different projects can connect different credentials.
    </p>
    <GuideCodeTabs :samples="samples.claudeCode" />

    <h3>Cursor</h3>
    <p>
      In Cursor, open <strong>Settings</strong> then <strong>Features</strong> then
      <strong>MCP Servers</strong> and add a new server:
    </p>
    <ul>
      <li><strong>Command:</strong> <code>npx -y @esignlaunchpad/mcp-server</code></li>
      <li><strong>Environment:</strong> <code>ESIGN_API_KEY=pat_YOUR_PERSONAL_ACCESS_TOKEN</code> (the env var keeps its historical name; its value should now be a PAT)</li>
    </ul>

    <h3>Environment variables</h3>
    <p>
      The server reads configuration from its environment. The only required variable is
      <code>ESIGN_API_KEY</code>.
    </p>
    <GuideCodeTabs :samples="samples.envVars" />

    <q-separator class="q-my-lg" />

    <h2>What you can do</h2>
    <p>
      Once connected, ask your agent in plain language. It selects and chains the right
      tools automatically.
    </p>
    <div class="guide-example">"Send a package named Q2 Contract to jane@acme.com and bob@acme.com for signing. Use the PDF at <code>./contract.pdf</code>."</div>
    <div class="guide-example">"List all packages that have been declined in the last 30 days."</div>
    <div class="guide-example">"Create a template from <code>./NDA.docx</code> with a single signer role called Counterparty, then send it to legal@partner.com."</div>
    <div class="guide-example">"Show me the audit trail for package <code>550e8400-e29b-41d4-a716-446655440000</code>."</div>
    <div class="guide-example">"Check if any webhook deliveries have failed today, and retry the dead-lettered ones."</div>
    <div class="guide-example">"How many packages did we send last month broken down by template?"</div>

    <q-separator class="q-my-lg" />

    <h2>Priming your AI agent</h2>
    <p>
      The agent auto-discovers the tool catalog on connect, but a small amount of
      platform-specific context dramatically improves its judgment. Copy the block below
      into your agent's project memory (<code>CLAUDE.md</code> for Claude Code, Cursor
      rules for Cursor, custom instructions for Claude Desktop). It is written as a
      system primer and is safe to paste verbatim.
    </p>
    <GuideCodeTabs :samples="samples.primerBlock" />

    <q-separator class="q-my-lg" />

    <h2>Safety notes for production</h2>
    <ul>
      <li>
        <strong>Never commit your <code>ESIGN_API_KEY</code>.</strong> Put it in
        <code>.env.local</code>, your OS keychain, or a secrets manager. MCP config
        files often land in version control. Verify your <code>.gitignore</code>
        before committing.
      </li>
      <li>
        <strong>Scope tokens narrowly.</strong> If the agent only needs to send packages,
        generate a PAT with <code>sign.package.create</code> + <code>sign.package.send</code>
        and no admin scopes. A read-only PAT using <code>sign.package.read</code> and
        <code>sign.template.read</code> is the safest choice for demos and evaluation.
      </li>
      <li>
        <strong>Think about accidental prompts</strong> before giving an agent a key
        that can delete, void, or archive packages. Agents chain tool calls, so a
        casual "clean up my drafts" can do more than you expect.
      </li>
      <li>
        <strong>Rate limits apply.</strong> The 120 requests-per-minute per-tenant
        limit counts MCP calls the same as direct HTTP calls. High-frequency agent
        workflows should batch where possible. See
        <a href="/Docs/IntegrationGuides?topic=errors">Errors &amp; Rate Limits</a>.
      </li>
      <li>
        <strong>Use test email addresses in prompts.</strong> Patterns like
        <code>someone+test@yourdomain.com</code> prevent accidental sends to real
        contacts while you iterate on prompt wording.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Tool catalog</h2>
    <p>
      Every platform endpoint is exposed as a tool. Names follow the pattern
      <code>verb_resource</code>, for example <code>create_package</code>,
      <code>list_signers</code>, <code>upload_document_from_path</code>. The server
      reads the OpenAPI spec at startup, so the tool list mirrors the API exactly. Use
      the <a href="/Docs/ApiReference">API Reference</a> as the canonical catalog.
      Every endpoint there has a corresponding MCP tool.
    </p>
    <p>Tools group into the following surfaces:</p>
    <ul>
      <li><strong>Packages.</strong> Create, send, void, archive, list, clone, bulk operations.</li>
      <li><strong>Documents.</strong> Upload direct, chunked, or import-from-URL. Download, tag detection.</li>
      <li><strong>Signers.</strong> Add, update, reassign, remove, reorder, resend notifications.</li>
      <li><strong>Annotations.</strong> Place, update, delete signing fields. Apply detected tags in bulk.</li>
      <li><strong>Templates.</strong> Template and role management. Instantiate packages from templates.</li>
      <li><strong>Signing groups.</strong> Reusable recipient rosters for team sign-offs.</li>
      <li><strong>Webhooks.</strong> Event-types discovery only. Webhook subscriptions are configured in the portal, not via the MCP server.</li>
      <li><strong>Audit and evidence.</strong> Retrieve trails, verify integrity, export in JSON, CSV, or PDF.</li>
      <li><strong>Tags.</strong> Build, parse, convert, and validate text tag strings.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Where to go next</h2>
    <ul>
      <li>
        <a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>.
        Scope your Personal Access Token correctly before handing it to an agent.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a>. Pair the agent
        with push-driven event handling instead of polling.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=errors">Errors &amp; Rate Limits</a>.
        Recover gracefully from 429s and transient failures.
      </li>
      <li>
        <a href="/Docs/ApiReference">API Reference</a>. Canonical tool catalog. Every
        endpoint maps to an MCP tool of the same name.
      </li>
    </ul>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const samples = {
    claudeDesktop: {
        json: `{
  "mcpServers": {
    "esign-launchpad": {
      "command": "npx",
      "args": ["-y", "@esignlaunchpad/mcp-server"],
      "env": {
        "ESIGN_API_KEY": "pat_YOUR_PERSONAL_ACCESS_TOKEN"
      }
    }
  }
}`
    },

    claudeCode: {
        json: `{
  "mcpServers": {
    "esign-launchpad": {
      "command": "npx",
      "args": ["-y", "@esignlaunchpad/mcp-server"],
      "env": {
        "ESIGN_API_KEY": "pat_YOUR_PERSONAL_ACCESS_TOKEN"
      }
    }
  }
}`
    },

    envVars: {
        bash: `# Required
ESIGN_API_KEY=pat_YOUR_PERSONAL_ACCESS_TOKEN

# Optional overrides (defaults shown)
ESIGN_API_URL=https://api.esignlaunchpad.com
ESIGN_SPEC_URL=https://api.esignlaunchpad.com/openapi/tenant-api.json`
    },

    // Paste-friendly priming block for Claude Code / Cursor rules / custom instructions.
    // Written as markdown so it renders sensibly in whichever UI displays it, with
    // second-person imperatives so an LLM reads it as behavioural rules rather than
    // documentation about the tools. The markdown list formatting in this primer is
    // intentional: it is content FOR an AI agent's memory, not documentation prose.
    primerBlock: {
        markdown: `## eSign Launchpad MCP

You have access to the eSign Launchpad e-signature platform via the \`esign-launchpad\` MCP
server. Use it to create, send, track, and retrieve signed documents programmatically.

### Rules

1. **Test-email pattern.** When testing or demoing, use \`anyname+test@anydomain.com\`-style
   addresses rather than real contacts so invitations don't go to production inboxes by
   accident.

2. **Package IDs are GUIDs.** Reference packages by the \`id\` field returned from creation
   calls, not by name. Names are not unique.

3. **Choosing an upload path:**
   - Local file on disk → \`upload_document_from_path\` (reads bytes directly, keeps your
     context window small).
   - File already at a public HTTPS URL → \`import_document_from_url\` (server fetches it
     directly, no bytes through your context).
   - Inline creation of a one-call package → \`create_and_send_package_json\` with a
     base64-encoded body. Only use this when no file path or URL is available.

4. **Never request the API key in chat.** The MCP server reads \`ESIGN_API_KEY\` from its
   environment; never ask the user to paste a key inline.

5. **Confirm before bulk or destructive operations.** Echo what you are about to do (delete,
   void, archive, bulk-add, reassign) and wait for the user's go-ahead. Draft deletion is
   irreversible; voiding a sent package is irreversible.

6. **Signer ordering.** With \`enforceSigningOrder: true\`, signers complete in
   \`routingOrder\` sequence. Authentication methods (SMS, KBA, ID verification) consume
   credits and may add cost per signer-confirm with the user before adding them.

7. **Answer state questions with a live fetch.** When the user asks about current status,
   call \`get_package\` right away and answer from the response. Do not say "I will wait for
   a webhook" - in an interactive session there is no server registered to receive one. This
   is a one-shot fetch in response to a prompt, not a polling loop. Long-running server
   integrations should still use webhooks as their primary signal; see the Webhooks guide.

8. **Rate limits.** The API enforces 120 requests/minute per tenant. Batch where possible
   (\`bulk_*\` tools exist for many surfaces). If a tool call returns a 429 with a
   \`Retry-After\` header, respect it.

### Tool surfaces

The server exposes 150+ tools across these categories:

- \`create_package\`, \`send_package\`, \`void_package\`, \`archive_package\`,
  \`list_packages\`, \`get_package\`, \`clone_package\`, plus bulk variants
- \`upload_document_from_path\`, \`import_document_from_url\`, chunked upload
  (\`initiate_document_upload\` + \`stage_document_block\` + \`commit_document_upload\`),
  \`get_signed_download_url\`, \`detect_document_tags\`, \`apply_detected_tags\`
- \`add_signer\`, \`add_signers_bulk\`, \`update_signer\`, \`reassign_signer\`,
  \`remove_signer\`, \`reorder_signers\`, \`resend_to_signer\`
- \`create_annotation\`, \`create_annotations_bulk\`, \`update_annotation\`,
  \`delete_annotation\`, \`validate_annotations\`
- \`create_template\`, \`list_templates\`, \`add_template_role\`,
  \`create_package_from_template\`
- \`create_signing_group\`, \`add_signing_group_member\`, \`list_signing_groups\`
- \`list_webhook_event_types\` (read-only discovery of event names). Webhook
  subscriptions, secrets, deliveries, retries, and tests are NOT in the MCP
  surface. They are portal-only. If the user asks you to create or manage a
  webhook, direct them to Settings → Webhooks in the portal.
- \`get_audit_trail\`, \`verify_audit_integrity\`, \`export_audit_trail\`,
  \`get_certificate_download_url\`
- \`build_tag\`, \`parse_tag\`, \`convert_tag\`, \`validate_tag\`, \`list_tag_types\`

Full endpoint details and schemas: https://api.esignlaunchpad.com (the OpenAPI spec that
backs every tool) - or open the API Reference at \`/Docs/ApiReference\` in the portal.`
    }
};

export default {
    name: 'GuideAiAgents',
    components: { GuideCodeTabs },
    data() {
        return { samples };
    }
};
</script>

<style scoped>
.guide-lead {
    font-size: 1.1rem;
    line-height: 1.7;
    color: #334155;
}

.guide-example {
    margin: 8px 0;
    padding: 10px 14px;
    background: #f8fafc;
    border-left: 3px solid #6366F1;
    border-radius: 0 6px 6px 0;
    color: #334155;
    line-height: 1.55;
}
</style>
